import { useEffect, useState } from 'react'
import { CheckIcon, Loader2Icon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fmt } from '@/constants/ui-locale'

interface BriefLoadingStateProps {
  projectName: string
  /** Already formatted in the reader's language, e.g. `12/10/2026`. */
  previousMeetingDate: string
  /** When generation started, so remounting the spinner does not restart the steps. */
  startedAt: number
}

type StepState = 'done' | 'active' | 'pending'

/** The minutes are read almost at once; the Backlog fetch is the slow middle. */
const MINUTES_DONE_AT_MS = 350
const TICKETS_DONE_AT_MS = 1150

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done') {
    return <CheckIcon className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
  }
  if (state === 'active') {
    return (
      <Loader2Icon
        className="h-4 w-4 shrink-0 text-[var(--accent)] motion-safe:animate-spin"
        aria-hidden="true"
      />
    )
  }
  return (
    <span
      className="block h-4 w-4 shrink-0 rounded-full border border-[var(--border-hover)]"
      aria-hidden="true"
    />
  )
}

function stepTextClass(state: StepState): string {
  if (state === 'active') return 'font-semibold text-[var(--accent)]'
  if (state === 'done') return 'text-[var(--text-primary)]'
  return 'text-[var(--text-tertiary)]'
}

/**
 * The four things the brief is doing, in order.
 *
 * The steps advance on a timer rather than on service callbacks because the
 * service is one request: splitting it into four would be a fiction. The
 * timeline is still honest about the order and roughly the shape of the wait.
 */
export default function BriefLoadingState({
  projectName,
  previousMeetingDate,
  startedAt,
}: BriefLoadingStateProps) {
  const { t } = useUiStrings()
  const [elapsed, setElapsed] = useState(() => Math.max(0, Date.now() - startedAt))

  useEffect(() => {
    const tick = () => setElapsed(Math.max(0, Date.now() - startedAt))
    tick()
    const timer = window.setInterval(tick, 200)
    return () => window.clearInterval(timer)
  }, [startedAt])

  const minutesDone = elapsed >= MINUTES_DONE_AT_MS
  const ticketsDone = elapsed >= TICKETS_DONE_AT_MS

  const steps: { label: string; state: StepState }[] = [
    { label: fmt(t.briefStepProject, { project: projectName }), state: 'done' },
    {
      label: fmt(t.briefStepMinutes, { date: previousMeetingDate }),
      state: minutesDone ? 'done' : 'active',
    },
    {
      label: t.briefStepTickets,
      state: ticketsDone ? 'done' : minutesDone ? 'active' : 'pending',
    },
    {
      label: t.briefStepSynthesize,
      state: ticketsDone ? 'active' : 'pending',
    },
  ]

  return (
    <div className="space-y-5">
      <ol className="space-y-2.5" aria-label={t.briefLoading}>
        {steps.map((step) => (
          <li key={step.label} className="flex items-center gap-2.5 text-xs">
            <StepIcon state={step.state} />
            <span className={stepTextClass(step.state)}>{step.label}</span>
          </li>
        ))}
      </ol>

      <p className="sr-only" role="status">
        {t.briefLoading}
      </p>

      {/* The skeleton is the shape of the answer, not decoration: it keeps the
          panel from collapsing and then shoving the footer down on arrival. */}
      <div className="space-y-3 pb-2" aria-hidden="true">
        <div className="h-2.5 w-full rounded-full bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
        <div className="h-2.5 w-3/5 rounded-full bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
        <div className="space-y-2.5 pt-3">
          <div className="h-2.5 w-1/4 rounded-full bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
          <div className="h-2.5 w-11/12 rounded-full bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
          <div className="h-2.5 w-4/5 rounded-full bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
        </div>
        <div className="space-y-2.5 pt-3">
          <div className="h-8 w-full rounded-lg bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
          <div className="h-8 w-full rounded-lg bg-[var(--bg-tertiary)] motion-safe:animate-pulse" />
        </div>
      </div>
    </div>
  )
}
